import { type Country, type Fandom } from "@prisma/client";
import { Form, message } from "antd";
import Decimal from "decimal.js";
import { type JsonRpcApiProvider, ethers } from "ethers";
import { useSession } from "next-auth/react";
import { use, useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { JoinedClubCard } from "~/components/Components/JoinedClubCard/JoinedClubCard";
import { type TokenCard } from "~/components/Components/TokenCard/TokenCard";
import { MyPageClubListModule } from "~/components/Modules/MyPage/MyPageClubListModule";
import { MyPageTokenListModule } from "~/components/Modules/MyPage/MyPageTokenListModule";
import { MyPageTemplate } from "~/components/Templates/MyPage/MyPageTemplate";
import { useAuth } from "~/hooks/useAuth";
import { useBalance } from "~/hooks/useBalance";
import { useConfig } from "~/hooks/useConfig";
import { useFileUpload } from "~/hooks/useFileUpload";
import { useInfiniteScroll } from "~/hooks/useInfiniteScroll";
import { useWallet } from "~/hooks/useWallet";
import { networkAtom, showLoaderOverlayAtom } from "~/state/atoms";
import { api } from "~/trpc/react";
import { RouterOutputs } from "~/trpc/shared";
import { copyToClipboard } from "~/utils/copyToClipBoard";
import { getImageUrlFromFileUrl } from "~/utils/file";
import { toFixed } from "~/utils/number";
import { shortenAddress } from "~/utils/shortenAddress";
import { getDpPoolContract, getERC20Contract } from "~/utils/web3/contract";
import { getProvider } from "~/utils/web3/eoa/provider";
import formatEther from "~/utils/web3/etherUnit";
import {
  getTransactionHistoryFromReceipt,
  waitForTransaction,
} from "~/utils/web3/transaction";

type FandomWithClubContract = RouterOutputs["fandom"]["getAllWithClubContract"];

export const MyPageContainer = () => {
  const [form] = Form.useForm();
  const { data: session, update } = useSession();
  const [userCountry, setUserCountry] = useState<Country>();
  const [originImageUrl, setOriginImageUrl] = useState<string>("");
  const [isChangeImageModalOpen, setIsChangeImageModalOpen] = useState(false);
  const [userJoinedFandoms, setUserJoinedFandoms] =
    useState<FandomWithClubContract>([]);
  const { pointBalance } = useBalance();
  // TODO: 추후 스왑 비율 나오면 수정
  const [swapRate, setSwapRate] = useState<number>(0);
  const [tokenList, setTokenList] = useState<
    React.ComponentProps<typeof TokenCard>[]
  >([]); // TODO(BS): 유저가 가지고 있는 토큰 리스트
  const [joinedClubList, setJoinedClubList] = useState<
    React.ComponentProps<typeof JoinedClubCard>[]
  >([]); // TODO(BS): 유저가 가입한 클럽 리스트
  const [allTokenPrices, setAllTokenPrices] = useState<
    RouterOutputs["token"]["getAllTokenPrices"]
  >([]);

  const setShowLoaderOverlay = useSetRecoilState(showLoaderOverlayAtom);
  // TODO: 추후 네트워크 변경 기능 추가 대비해서 targetNetwork recoil 값 사용중 > 기능 들어오면 SelectNetworkDropdown 컴포넌트 사용
  const targetNetwork = useRecoilValue(networkAtom);

  const { address } = useWallet();
  const { namuFiles, handleRemove, beforeUpload } = useFileUpload({
    form: form,
    initialValues: { imageUrl: "" },
  });
  const {
    datas: points,
    hasMoreData,
    isLoading,
    setDatas,
    setLastTimestamp,
  } = useInfiniteScroll({
    dataType: "POINT",
    take: 10,
    userId: session?.user?.id,
  });

  const { datas: txHistory, resetDatas } = useInfiniteScroll({
    dataType: "TOKEN_HISTORY",
    take: 20,
    userId: session?.user?.id,
  });
  const { getConfigValue } = useConfig();

  const aaGasFee = parseInt(getConfigValue("aaTokenFee")) ?? 50;
  const imageFile = namuFiles.find((file) => file.fieldName === "imageUrl");

  const apiGetCountries = api.common.getCountries.useQuery();
  const apiGetFandoms = api.fandom.getAllWithClubContract.useQuery();
  const apiGetUserLatestReward = api.club.getUserLatestReward.useQuery(
    {
      walletAddress: session?.user?.walletAddress ?? "",
    },
    {
      enabled: !!session?.user?.walletAddress,
    },
  );
  const updateTokenHistory = api.token.updateTokenHistory.useMutation();
  const apiGetAllTokenPrice = api.token.getAllTokenPrices.useQuery();
  const apiUpdateUser = api.user.update.useMutation({
    onSuccess: async (data) => {
      handleRemove(imageFile!);
      setOriginImageUrl(data.imageUrl ?? "");
    },
  });
  const apiGetNetworkTokens = api.token.getNetworkTokens.useQuery({
    network: targetNetwork,
  });
  const apiGetSwapRatio = api.swap.calculateFANDtoFAORatio.useQuery(undefined, {
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (session?.user?.imageUrl) {
      setOriginImageUrl(session?.user?.imageUrl);
    }
  }, []);

  useEffect(() => {
    if (imageFile?.fileUrl.startsWith("blob")) {
      setIsChangeImageModalOpen(true);
    }
  }, [imageFile]);

  useEffect(() => {
    if (apiGetSwapRatio.data) {
      setSwapRate(1 / apiGetSwapRatio.data);
    }
  }, [apiGetSwapRatio.data]);

  useEffect(() => {
    if (apiGetCountries.data && session?.user?.countryId) {
      const countries = apiGetCountries.data;
      const userCountry = countries.find(
        (country) => country.id.toString() == session?.user?.countryId,
      );
      setUserCountry(userCountry);
    }
  }, [apiGetCountries.data, session?.user?.countryId]);

  useEffect(() => {
    if (apiGetAllTokenPrice.data) {
      setAllTokenPrices(apiGetAllTokenPrice.data);
    }
  }, [apiGetAllTokenPrice.data]);

  useEffect(() => {
    if (apiGetFandoms.data && session?.user?.myFandomIds) {
      const joinedFandoms = apiGetFandoms.data.filter(
        (fandom) => session?.user?.myFandomIds.includes(fandom.id),
      );
      setUserJoinedFandoms(joinedFandoms);
    }
  }, [apiGetFandoms.data, session?.user?.myFandomIds]);

  // TODO(BS): 클럽 탭에 들어갈 데이터를 추가하는 곳입니다
  useEffect(() => {
    // TODO(BS): 임시로 유저가 가입한 모든 팬덤 리스트를 사용중입니다 > 유저가 가입한 클럽 리스트로 변경해야합니다.
    if (!apiGetUserLatestReward.data) return;

    const provider = getProvider();
    if (userJoinedFandoms.length > 0) {
      const joinedClubList = userJoinedFandoms.map(async (fandom) => {
        if (!fandom.ClubContract) return { fandom: fandom as Fandom };

        const clubContractAddress = fandom.ClubContract.contractAddress;
        const clubContract = getDpPoolContract(clubContractAddress, provider);

        const [balance, unstakeRequests, isUnstaking] = await Promise.all([
          clubContract.balanceOf?.(address),
          clubContract.unstakeRequests?.(address),
          clubContract.isUnstaking?.(address),
        ]);

        const result: React.ComponentProps<typeof JoinedClubCard> = {
          fandom: fandom as Fandom,
        };

        if (isUnstaking) {
          const requestUnstakeTime = new Date(Number(unstakeRequests) * 1000);

          // TODO(BS): 테스트 코드라 돌려놔야 함.
          // // unstake 가능한 시간 설정
          // const unstakeTime =
          //   requestUnstakeTime.getTime() + 14 * 24 * 60 * 60 * 1000;

          const yesKevinDuration = 5 * 60 * 1000;
          const yesKevinCondition =
            fandom.ClubContract.implementationAddress ===
            "0x3c9beca86d261be8e973C277adcc9305bD78F518";

          // unstake 가능한 시간 설정
          const unstakeTime =
            requestUnstakeTime.getTime() +
            (yesKevinCondition ? yesKevinDuration : 14 * 24 * 60 * 60 * 1000);

          result.unstakeProps = {
            stakeAmount: formatEther(balance),
            stakeAmountPrice:
              toFixed(
                allTokenPrices.find((price) => price.symbol === "FAND")?.price,
                6,
              ) * Number(ethers.formatEther(balance)),
            claimTimestamp: unstakeTime,
          };
        } else if (BigInt(balance) > 0n) {
          const totalRewardAmount = apiGetUserLatestReward.data
            .filter(
              (reward) => reward?.clubContractId === fandom.clubContractId,
            )
            .reduce(
              (acc, cur) => acc.add(cur?.totalAmount ?? 0),
              new Decimal(0),
            );
          result.stakeProps = {
            stakeAmount: formatEther(balance),
            stakeAmountPrice:
              toFixed(
                allTokenPrices.find((price) => price.symbol === "FAND")?.price,
                6,
              ) * Number(ethers.formatEther(balance)),
            totalRewardAmount: totalRewardAmount.toNumber(),
          };
        }

        return result;
      });

      void Promise.all(joinedClubList).then((result) => {
        const clubFiltered = result.filter(
          (v) => v.stakeProps || v.unstakeProps,
        );
        setJoinedClubList(clubFiltered);
      });
    }
  }, [userJoinedFandoms, apiGetUserLatestReward.data, address]);

  const fetchImageUrl = async () => {
    if (imageFile?.fileUrl.startsWith("blob")) {
      setShowLoaderOverlay(10000);
      try {
        const uploadedImageUrl = await getImageUrlFromFileUrl(
          imageFile?.fileUrl,
        );

        if (!session?.user?.id) {
          return;
        }

        if (uploadedImageUrl) {
          apiUpdateUser.mutate({
            id: session?.user?.id,
            imageUrl: uploadedImageUrl,
          });
        }
      } catch (error) {
        void message.error("Fail to Upload Image");
      } finally {
        setShowLoaderOverlay(false);
      }
    }
  };

  // useEffect(() => {
  //   if (txHistory.length <= 0) return;
  //   if (!session?.user?.walletAddress) return;
  //   const provider = getProvider();
  //   const promises = txHistory.map(async (tx) => {
  //     if (!tx.transactionHash) return;
  //     if (tx.status !== "Pending" || tx.status !== "Delay") return;

  //     const isAA = session.user.loginType === "WEB3_AUTH";

  //     const receipt = await waitForTransaction(provider, tx.transactionHash);
  //     if (!receipt) {
  //       updateTokenHistory.mutate({
  //         tokenHistoryId: tx.id,
  //         status: "Fail",
  //       });
  //       return;
  //     }

  //     if (receipt.status !== 1) {
  //       updateTokenHistory.mutate({
  //         tokenHistoryId: tx.id,
  //         status: "Fail",
  //         usedGasFee: isAA ? aaGasFee : undefined,
  //       });

  //       return;
  //     }

  //     const updates = await getTransactionHistoryFromReceipt(
  //       provider,
  //       receipt,
  //       isAA ? aaGasFee : undefined,
  //     );
  //     // updateTransactionHistory(tx.transactionHash, updates);
  //     updateTokenHistory.mutate({ ...updates, tokenHistoryId: tx.id });
  //   });
  //   void Promise.all(promises).then((p) => {
  //     const result = p.filter((v) => v);
  //     if (result.length > 0) {
  //       resetDatas();
  //     }
  //   });
  // }, [resetDatas, txHistory]);

  // TODO(BS): Send 페이지에서 상민님이 만들어놨던 tokenList 불러오는 기능 그대로 긁어왔습니다. 확인해주시기 바랍니다.
  useEffect(() => {
    if (!apiGetNetworkTokens.data) return;
    if (!address) return;

    let provider: JsonRpcApiProvider;

    const promises = apiGetNetworkTokens.data.map(async (token: any) => {
      if (!provider) provider = new ethers.JsonRpcProvider(token.rpcUrl);

      if (!token.contractAddress) {
        // Native
        const balance = await provider.getBalance(address);
        return {
          tokenTicker: token.name,
          userAmount: toFixed(ethers.formatEther(balance), 3),
          totalPrice:
            toFixed(
              allTokenPrices.find((price) => price.symbol === token.name)
                ?.price,
              6,
            ) * Number(ethers.formatEther(balance)),
        };
      } else {
        // ERC20
        const contract = getERC20Contract(token.contractAddress, provider);

        const balance = await contract.balanceOf?.(address);
        const decimals = await contract.decimals?.();

        return {
          tokenTicker: token.name,
          userAmount: toFixed(ethers.formatUnits(balance, decimals), 3),
          totalPrice:
            toFixed(
              allTokenPrices.find((price) => price.symbol === token.name)
                ?.price,
              6,
            ) * Number(ethers.formatEther(balance)),
        };
      }
    });

    void Promise.all(promises).then((result) => {
      setTokenList(result as any);
    });
  }, [apiGetNetworkTokens.data, address]);

  const onOkModal = async () => {
    setIsChangeImageModalOpen(false);
    await fetchImageUrl();
  };

  const onCancelModal = () => {
    if (imageFile?.fileUrl) {
      handleRemove(imageFile);
    }
    setIsChangeImageModalOpen(false);
  };

  const mypageTemplateProps: React.ComponentProps<typeof MyPageTemplate> = {
    myPageTopBarProps: {
      topBarProps: {
        title: "My Page",
        topBarIconProps: [
          {
            iconName: "setting",
            href: "/setting",
          },
          {
            iconName: "history",
            href: "/pointHistory",
          },
        ],
      },
    },
    myPageUserProfileProps: {
      imageUrl: originImageUrl,
      nickname: session?.user?.nickname ?? "",
      countryName: userCountry?.name ?? "",
      addressCopyButtonProps: {
        userWalletAddress: session?.user?.walletAddress
          ? shortenAddress(session?.user?.walletAddress)
          : "",
        onClickCopy: () => {
          void copyToClipboard(session?.user?.walletAddress ?? "");
        },
      },
      changeProfileImageFormProps: {
        form: form,
        originImageUrl: originImageUrl,
        imageFile: imageFile,
        handleRemove: handleRemove,
        beforeUpload: beforeUpload,
        fieldName: `imageUrl`,
        className: "aspect-1 w-full ",
        required: false,
        isRound: true,
      },
      changeProfileImageModalProps: {
        isModalOpen: isChangeImageModalOpen,
        onCancelModal: onCancelModal,
        onOkModal: onOkModal,
        title: "Change Profile Image?",
      },
    },
    myPageJoinedFandomsProps: {
      joinedFandoms: userJoinedFandoms,
    },
    myPageAssetProps: {
      pointBalance: toFixed(Number(pointBalance) ?? 0, 3),
      swapBalance: toFixed(
        pointBalance ? Number(pointBalance.mul(swapRate)) : 0,
        5,
      ),
      // TODO: web3 페이지 연결 버튼들 > 여기만 주석처리하면 기능 on/off 가능
      routes: tokenPageRoutes,
    },
    myPageHistoryProps: {
      tabBarProps: {
        // TODO
        // 토큰 발행 이전 주석 > MyPage 모듈 주석처리
        // 토큰 발행 이후 주석 > PointHistory 모듈 주석처리
        items: [
          {
            label: "Token",
            key: "token",
            content: <MyPageTokenListModule tokenCardListProps={tokenList} />,
          },
          {
            label: "Club",
            key: "club",
            content: (
              <MyPageClubListModule joinedClubCardListProps={joinedClubList} />
            ),
          },
          // {
          //   label: "Point",
          //   key: "point",
          //   content: <PointHistoryPointListModule pointHistoryList={points} />,
          // },
          // {
          //   label: "Token",
          //   key: "token",
          //   content: (
          //     <PointHistoryTokenListModule tokenHistoryList={txHistory} />
          //   ),
          // },
        ],
        tabBarStyle: {
          backgroundColor: "white",
          padding: "0px",
          paddingLeft: "20px",
          paddingRight: "20px",
          marginBottom: "0px",
          position: "sticky",
          top: "54px",
          zIndex: 11,
        },
      },
    },
  };

  return (
    <>
      <MyPageTemplate {...mypageTemplateProps} />
    </>
  );
};

const tokenPageRoutes: React.ComponentProps<
  typeof MyPageTemplate
>["myPageAssetProps"]["routes"] = [
  {
    icon: "swap",
    routeLabel: "SWAP",
    href: "/swap",
  },
  // {
  //   icon: "shop",
  //   routeLabel: "Buy&Sell",
  //   href: "https://uniswap.org/",
  //   isNewTab: true,
  // },
  {
    icon: "rightArrow",
    routeLabel: "Send",
    href: "/send",
  },
  {
    icon: "history",
    routeLabel: "History",
    href: "/pointHistory",
  },
];
